import {
  applyMiddleware,
  combineReducers,
  legacy_createStore as createStore,
} from 'redux';
import Kefir, {
  type Emitter,
  type Observable,
  type Property,
} from 'kefir';
import createSagaMiddleware from 'redux-saga';
import type { Saga, SagaMonitor, Task } from 'redux-saga';
import {
  type PreloadedStoreState,
  type NormalizedSelectorTracingOptions,
  type NormalizedStoreOptions,
  type ReducersMap,
  type StoreOptions,
  type StoreMiddleware,
  type StoreReducerFunction,
  type StoreState,
  type StoreStateFromStateMap,
  type StoreStateMap,
  type SelectorTraceSummary,
  type SelectorCadenceTraceEvent,
  type SelectorDetailTraceEvent,
  type SagaMonitorTraceEvent,
  type RuntimeErrorTraceEvent,
  type ReduxActionTraceEvent,
  type StoreTraceStreams,
  type SelectorTraceAggregate,
  type SelectorTracePeriodSummary,
} from './types';
import { createLoggerMiddleware } from './redux-logger';
import type { ReduxStoreContext } from './internal-types';
import {
  INTERNAL_SAGA_MANAGER_NAME,
  INTERNAL_STORE_UTILITY_DOMAIN,
} from './constants';
import { sagaManager } from './slices/saga-manager/sagas/manager';
import {
  sagaManagerReducer,
  startSaga,
  stopSaga,
} from './slices/saga-manager/saga-manager-slice';
import type { SagaCrashState } from './slices/saga-manager/saga-manager-slice';
import { storeUtilityReducer } from './slices/store-utility/store-utility-slice';
import type { StoreUtilityState } from './slices/store-utility/store-utility-slice';
import { registerGlobalDevTools } from './global-dev-tools';
import { deriveSagaName } from './utils/sagas/derive-saga-name';
import { normalizeSelectorTracingOptions, normalizeStoreOptions } from './store-options';
import { createSelectorCadenceSource } from './utils/selector-core/throttled-selector-options';
import { renderAccessedPaths } from './utils/selector-core/create-cached-selector';
import { evictSelectorOutputsForStateSource } from './utils/selector-core/selector-output-cache';
import { registerSelectorTracingBridge } from './utils/selector-core/selector-tracing-bridge';
import {
  getEmptySelectorTraceSummary,
  SelectorTraceSummaryCollector,
} from './selector-trace-summary';
import type {
  CachedSelector,
  SelectorCadenceSource,
  SelectorComputationTraceOptions,
  SelectorTrace,
  SelectorTraceReporter,
} from './utils/types';

const MAX_SELECTOR_SOURCE_SNIPPET_LINES = 5;
const MAX_SELECTOR_SOURCE_SNIPPET_LENGTH = 500;

type StateDiff = Record<string, { prev: unknown; next: unknown }>;

class ChangesPayload {
  readonly #prevState: unknown;
  readonly #nextState: unknown;

  constructor(prevState: unknown, nextState: unknown) {
    this.#prevState = prevState;
    this.#nextState = nextState;
  }

  get changes(): StateDiff {
    return createStateDiff(this.#prevState, this.#nextState);
  }
}

const isPrimitive = (
  value: unknown
): value is string | number | bigint | boolean | symbol | null | undefined => {
  return value === null || (typeof value !== 'object' && typeof value !== 'function');
};

const getReduxActionTitle = (action: unknown): string => {
  const actionType =
    typeof action === 'object' && action !== null && 'type' in action
      ? (action as { type: unknown }).type
      : action;
  const hasPayload = typeof action === 'object' && action !== null && 'payload' in action;
  if (!hasPayload) return String(actionType);
  const payload = (action as { payload: unknown }).payload;
  if (Array.isArray(payload) && payload.length === 1 && isPrimitive(payload[0])) {
    return `${String(actionType)} ${String(payload[0])}`;
  }
  return isPrimitive(payload) ? `${String(actionType)} ${String(payload)}` : String(actionType);
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const addStateDiff = (
  changes: StateDiff,
  prevValue: unknown,
  nextValue: unknown,
  path: string
): void => {
  if (Object.is(prevValue, nextValue)) return;
  if (prevValue === undefined) {
    changes[path || '<root>'] = { prev: undefined, next: nextValue };
    return;
  }
  if ((Array.isArray(prevValue) || prevValue === undefined) &&
      (Array.isArray(nextValue) || nextValue === undefined)) {
    const prevArray = Array.isArray(prevValue) ? prevValue : [];
    const nextArray = Array.isArray(nextValue) ? nextValue : [];
    const length = Math.max(prevArray.length, nextArray.length);
    if (length > 0) {
      for (let index = 0; index < length; index += 1) {
        addStateDiff(
          changes,
          prevArray[index],
          nextArray[index],
          path ? `${path}[${index}]` : `[${index}]`
        );
      }
      return;
    }
  }
  if ((isPlainRecord(prevValue) || prevValue === undefined) &&
      (isPlainRecord(nextValue) || nextValue === undefined)) {
    const prevRecord = isPlainRecord(prevValue) ? prevValue : {};
    const nextRecord = isPlainRecord(nextValue) ? nextValue : {};
    const keys = new Set([...Object.keys(prevRecord), ...Object.keys(nextRecord)]);
    if (keys.size > 0) {
      for (const key of keys) {
        addStateDiff(
          changes,
          prevRecord[key],
          nextRecord[key],
          path ? `${path}.${key}` : key
        );
      }
      return;
    }
  }
  changes[path || '<root>'] = { prev: prevValue, next: nextValue };
};

const createStateDiff = (prevState: unknown, nextState: unknown): StateDiff => {
  const changes: StateDiff = {};
  addStateDiff(changes, prevState, nextState, '');
  return changes;
};

let hasLoggedReduxWelcomeMessage = false;

const getSelectorSourceSnippet = (selectorFunc: CachedSelector<any, any, any[]>): string => {
  return selectorFunc
    .toString()
    .split(/\r?\n/)
    .slice(0, MAX_SELECTOR_SOURCE_SNIPPET_LINES)
    .join('\n')
    .slice(0, MAX_SELECTOR_SOURCE_SNIPPET_LENGTH);
};

type DefaultStoreReducersMap = {
  [INTERNAL_STORE_UTILITY_DOMAIN]: typeof storeUtilityReducer;
  [INTERNAL_SAGA_MANAGER_NAME]: typeof sagaManagerReducer;
};

type DefaultStoreStateMap = {
  [INTERNAL_STORE_UTILITY_DOMAIN]: StoreUtilityState;
  [INTERNAL_SAGA_MANAGER_NAME]: SagaCrashState;
};

type RuntimeStoreStateStream<TState> = {
  observable: Property<TState, never>;
  getSnapshot(): TState;
  dispose(): void;
};

const createCadencedStoreStateStream = <TState>(
  store: ReduxStoreContext['store'],
  selectorCadenceSource: SelectorCadenceSource
): RuntimeStoreStateStream<TState> => {
  let currentState = store.getState() as TState;
  let lastEmitted = currentState;
  let activeEmitter: Emitter<TState, never> | undefined;
  let activeCleanup: (() => void) | undefined;
  let disposed = false;

  const readCurrentState = (): TState => {
    currentState = store.getState() as TState;
    return currentState;
  };

  const emitLatest = (): void => {
    if (disposed || !activeEmitter) {
      return;
    }
    const latestState = readCurrentState();
    if (latestState !== lastEmitted) {
      lastEmitted = latestState;
      activeEmitter.value(latestState);
    }
  };

  const observable = Kefir.stream<TState, never>((emitter) => {
    if (disposed) {
      emitter.end();
      return;
    }

    currentState = store.getState() as TState;
    lastEmitted = currentState;
    activeEmitter = emitter;
    const unsubscribeStore = store.subscribe(() => {
      readCurrentState();
      selectorCadenceSource.requestTick();
    });
    const unsubscribeCadence = selectorCadenceSource.subscribe(emitLatest);

    activeCleanup = () => {
      unsubscribeCadence();
      unsubscribeStore();
      activeEmitter = undefined;
      activeCleanup = undefined;
    };

    return activeCleanup;
  }).toProperty(() => {
    const snapshot = readCurrentState();
    lastEmitted = snapshot;
    return snapshot;
  });

  return {
    observable,
    getSnapshot: readCurrentState,
    dispose() {
      disposed = true;
      activeEmitter?.end();
      activeCleanup?.();
      activeEmitter = undefined;
      activeCleanup = undefined;
    },
  };
};

export type StoreReducersInput<TStateMap extends StoreStateMap> = {
  [Domain in keyof TStateMap]: StoreReducerFunction<TStateMap[Domain]>;
};

export type StoreReducersMap<
  TStateMap extends StoreStateMap,
  TReducers extends StoreReducersInput<TStateMap> = StoreReducersInput<TStateMap>,
> = DefaultStoreReducersMap & TReducers;

export type StoreBoundState<TStateMap extends StoreStateMap> = StoreStateFromStateMap<
  DefaultStoreStateMap & TStateMap
>;
export type StoreMiddlewareInput = StoreMiddleware | StoreMiddleware[];

const createStoreSagaMonitor = (
  publish: (event: SagaMonitorTraceEvent) => void
): SagaMonitor => ({
  effectTriggered(event) {
    publish({ type: 'effectTriggered', event });
  },
  effectResolved(effectId, result) {
    publish({ type: 'effectResolved', effectId, result });
  },
  effectRejected(effectId, error) {
    publish({ type: 'effectRejected', effectId, error });
  },
  effectCancelled(effectId) {
    publish({ type: 'effectCancelled', effectId });
  },
  actionDispatched(action) {
    publish({ type: 'actionDispatched', action });
  },
});

export abstract class StoreRuntime<
  TStateMap extends StoreStateMap = {},
  TReducers extends StoreReducersInput<TStateMap> = StoreReducersInput<TStateMap>,
> {
  private reducers: StoreReducersMap<TStateMap, TReducers> = {
    [INTERNAL_STORE_UTILITY_DOMAIN]: storeUtilityReducer,
    [INTERNAL_SAGA_MANAGER_NAME]: sagaManagerReducer,
  } as StoreReducersMap<TStateMap, TReducers>;
  private middlewares: StoreMiddleware[] = [];
  private readonly sagaMiddleware: ReturnType<typeof createSagaMiddleware>;
  private readonly reduxLoggerMiddleware: StoreMiddleware | undefined;
  private tasksStarted: Task[] = [];
  private storeContext: ReduxStoreContext | undefined;
  private selectorCadenceSource: SelectorCadenceSource | undefined;
  private cadencedStoreStateStream:
    | RuntimeStoreStateStream<StoreBoundState<TStateMap>>
    | undefined;
  private disposeDevTools: (() => void) | undefined;
  private selectorTracingEnabled = false;
  private readonly selectorTraceReporter: SelectorTraceReporter<any, any, any[]> | undefined;
  private selectorTraceSummaryCollector: SelectorTraceSummaryCollector | undefined;
  private selectorTraceSummaryInterval: ReturnType<typeof setInterval> | undefined;
  private selectorTracingOptions: NormalizedSelectorTracingOptions;
  private readonly legacySelectorTracingActivationAllowed: boolean;
  protected readonly storeOptions: NormalizedStoreOptions;
  private readonly traceEmitters = {
    selectorDetail: Kefir.pool<SelectorDetailTraceEvent, never>(),
    selectorSummary: Kefir.pool<SelectorTraceSummary, never>(),
    selectorCadence: Kefir.pool<SelectorCadenceTraceEvent, never>(),
    sagaMonitor: Kefir.pool<SagaMonitorTraceEvent, never>(),
    runtimeError: Kefir.pool<RuntimeErrorTraceEvent, never>(),
    reduxAction: Kefir.pool<ReduxActionTraceEvent, never>(),
  };
  readonly traceStreams: StoreTraceStreams = Object.freeze({
    selectorDetail: this.traceEmitters.selectorDetail.map((event) => event),
    selectorSummary: this.traceEmitters.selectorSummary.map((event) => event),
    selectorCadence: this.traceEmitters.selectorCadence.map((event) => event),
    sagaMonitor: this.traceEmitters.sagaMonitor.map((event) => event),
    runtimeError: this.traceEmitters.runtimeError.map((event) => event),
    reduxAction: this.traceEmitters.reduxAction.map((event) => event),
  });
  private loggerDisposer: (() => void) | undefined;
  private loggerSubscriptions: Array<{ unsubscribe(): void }> = [];

  constructor(
    reducersMap?: TReducers & StoreReducersInput<TStateMap>,
    middleware?: StoreMiddlewareInput,
    options?: StoreOptions
  ) {
    this.storeOptions = normalizeStoreOptions(options);
    this.selectorTracingOptions = this.storeOptions.traceSelectors;
    this.legacySelectorTracingActivationAllowed =
      options?.traceSelectors === undefined || options.traceSelectors === false;
    this.selectorTraceSummaryCollector = this.selectorTracingOptions.summaryEnabled
      ? new SelectorTraceSummaryCollector(getSelectorSourceSnippet)
      : undefined;
    this.selectorTracingEnabled =
      (
        this.selectorTracingOptions.traceExecution ||
        this.selectorTracingOptions.traceCache ||
        this.selectorTracingOptions.traceInvalidation ||
        this.selectorTracingOptions.traceArguments ||
        this.selectorTracingOptions.traceResults ||
        this.selectorTraceSummaryCollector !== undefined
      );
    this.selectorTraceReporter = (trace) => this.reportSelectorTrace(trace);
    if (this.selectorTracingEnabled) {
      this.registerSelectorTracingBridge();
    }
    this.sagaMiddleware = this.storeOptions.sagaMonitor
      ? createSagaMiddleware({ sagaMonitor: createStoreSagaMonitor((event) => this.publishTrace('sagaMonitor', event)) })
      : createSagaMiddleware();
    this.reduxLoggerMiddleware = this.storeOptions.logReduxActions
      ? createLoggerMiddleware((event) => this.publishTrace('reduxAction', event))
      : undefined;
    for (const [name, reducer] of Object.entries(reducersMap ?? {})) {
      this.registerReducer(name, reducer);
    }
    if (middleware !== undefined) {
      this.addMiddleware(middleware);
    }
  }

  private registerReducer(name: string, reducer: StoreReducerFunction): void {
    const reducers = this.reducers as ReducersMap;
    if (reducers[name] !== undefined) {
      throw new Error(
        `Reducer "${name}" is already added. Each reducer name must be unique.`
      );
    }
    reducers[name] = reducer;
  }

  addMiddleware(middleware: StoreMiddlewareInput): void {
    if (Array.isArray(middleware)) {
      this.middlewares.push(...middleware);
    } else {
      this.middlewares.push(middleware);
    }
  }

  private getMiddlewarePipeline(): StoreMiddleware[] {
    return [
      ...this.middlewares,
      ...(this.reduxLoggerMiddleware ? [this.reduxLoggerMiddleware] : []),
      this.sagaMiddleware,
    ];
  }

  private runSagaSafely<S extends Saga>(saga: S, ...args: Parameters<S>): Task {
    const task = this.sagaMiddleware.run(saga, ...args);
    this.tasksStarted.push(task);
    return task;
  }

  private createExtendedDefaultState(initialState?: PreloadedStoreState): StoreState {
    const reducers = this.reducers as ReducersMap;

    return Object.keys(reducers).reduce<StoreState>((state, domain) => {
      return {
        ...state,
        [domain]: {
          ...(reducers[domain] as any).initialState,
          ...(initialState ? initialState[domain] : {}),
        },
      };
    }, {} as StoreState);
  }

  private stopSagas(): void {
    for (const task of this.tasksStarted) {
      task.cancel();
    }
    this.tasksStarted = [];
  }

  private getOrCreateSelectorCadenceSource(): SelectorCadenceSource {
    if (!this.selectorCadenceSource) {
      const cadenceSource = createSelectorCadenceSource(
        this.storeOptions.throttledSelectorFrequency,
        {
          traceSelectors: this.selectorTracingOptions.traceCadence,
          onTick: this.selectorTracingOptions.traceCadence
            ? (timestamp, listenerCount) => {
                this.publishTrace('selectorCadence', { type: 'tick', timestamp, listenerCount });
              }
            : undefined,
          onSubscribe: this.selectorTracingOptions.traceCadence
            ? (listenerCount) => {
                this.publishTrace('selectorCadence', { type: 'subscribe', listenerCount });
              }
            : undefined,
        }
      );
      this.selectorCadenceSource = cadenceSource;
    }

    return this.selectorCadenceSource;
  }

  private disposeSelectorCadenceSource(): void {
    this.selectorCadenceSource?.dispose();
    this.selectorCadenceSource = undefined;
  }

  private disposeCadencedStoreStateStream(): void {
    this.cadencedStoreStateStream?.dispose();
    this.cadencedStoreStateStream = undefined;
  }

  private publishTrace(stream: keyof StoreTraceStreams, event: unknown): void {
    const immutableEvent =
      event !== null && typeof event === 'object' ? Object.freeze(event) : event;
    (this.traceEmitters[stream] as any).plug(Kefir.constant(immutableEvent));
  }

  reportRuntimeError(
    error: unknown,
    source?: string,
    message?: string,
    payload?: unknown
  ): void {
    this.publishTrace('runtimeError', {
      error,
      source,
      message,
      ...(payload === undefined ? {} : { payload }),
    });
  }

  private attachLogger(): void {
    this.disposeLogger();
    const loggerFactory = this.storeOptions.loggerFactory;
    if (loggerFactory) {
      this.loggerDisposer = loggerFactory(this.traceStreams) || undefined;
      return;
    }

    if (this.storeOptions.logReduxActions && !hasLoggedReduxWelcomeMessage) {
      hasLoggedReduxWelcomeMessage = true;
      console.log(
        `%c🔧 Redux Logger Active%c\n\n%cLegend:%c\n  %c■%c State changed (bold title)\n  %c■%c No state change (gray title)\n\n%cLog labels:%c\n  %cprev state%c  — state before action\n  %caction%c      — dispatched action\n  %cnext state%c  — state after action\n  %cstate%c       — lazy state/diff payload (expanded by default)\n  %cstate (no changes)%c — state unchanged`,
        'color: #03A9F4; font-weight: bold; font-size: 14px', '',
        'color: #888; font-weight: bold', '', 'color: inherit; font-weight: 600', '',
        'color: #9E9E9E; font-weight: 300', '', 'color: #888; font-weight: bold', '',
        'color: #9E9E9E; font-weight: bold', '', 'color: #03A9F4; font-weight: bold', '',
        'color: #4CAF50; font-weight: bold', '', 'color: #4CAF50; font-weight: bold', '',
        'color: #9E9E9E; font-weight: lighter', ''
      );
    }

    this.loggerSubscriptions = [
      this.traceStreams.selectorDetail.observe((event) => {
        const { kind: _kind, ...payload } = event;
        console.info('[themis] selector trace', payload);
      }),
      this.traceStreams.selectorCadence.observe((event) => {
        if (event.type === 'subscribe') {
          console.info('SUBSCRIBE SELECTOR CADENCE', event.listenerCount);
        } else {
          console.info('SELECTOR CADENCE TICK', event.timestamp, event.listenerCount);
        }
      }),
      this.traceStreams.sagaMonitor.observe((event) => {
        switch (event.type) {
          case 'effectTriggered':
            console.info('[themis:saga] effectTriggered', event.event);
            break;
          case 'effectResolved':
            console.info('[themis:saga] effectResolved', event.effectId, event.result);
            break;
          case 'effectRejected':
            console.info('[themis:saga] effectRejected', event.effectId, event.error);
            break;
          case 'effectCancelled':
            console.info('[themis:saga] effectCancelled', event.effectId);
            break;
          case 'actionDispatched':
            console.info('[themis:saga] actionDispatched', event.action);
            break;
        }
      }),
      this.traceStreams.runtimeError.observe((event) => {
        console.error('[themis] runtime error', event.error, event);
      }),
      this.traceStreams.reduxAction.observe((event) => {
        console.groupCollapsed(
          `%c${getReduxActionTitle(event.action)}`,
          event.stateChanged
            ? 'color: inherit; font-weight: 600'
            : 'color: #9E9E9E; font-weight: 300'
        );
        console.log('%c action    ', 'color: #03A9F4; font-weight: bold', event.action);
        if (event.stateChanged) {
          console.log(
            '%c state    ',
            'color: #4CAF50; font-weight: bold',
            new ChangesPayload(event.prevState, event.nextState)
          );
        } else {
          console.log(
            '%c state (no changes)',
            'color: #9E9E9E; font-weight: lighter',
            { state: event.nextState }
          );
        }
        console.groupEnd();
      }),
    ];
  }

  private disposeLogger(): void {
    for (const subscription of this.loggerSubscriptions) {
      subscription.unsubscribe();
    }
    this.loggerSubscriptions = [];
    this.loggerDisposer?.();
    this.loggerDisposer = undefined;
  }

  getStoreStateStream(): Observable<StoreBoundState<TStateMap>, any> {
    if (!this.cadencedStoreStateStream) {
      throw new Error(
        'Cannot access StoreRuntime.getStoreStateStream() before Store.init() has been called.'
      );
    }

    return this.cadencedStoreStateStream.observable;
  }

  getStoreStateSnapshot(): StoreBoundState<TStateMap> {
    if (!this.cadencedStoreStateStream) {
      throw new Error(
        'Cannot access StoreRuntime.getStoreStateSnapshot() before Store.init() has been called.'
      );
    }

    return this.cadencedStoreStateStream.getSnapshot();
  }

  getReducers(): StoreReducersMap<TStateMap, TReducers> {
    return { ...this.reducers };
  }

  get state(): StoreBoundState<TStateMap> {
    if (!this.storeContext) {
      throw new Error(
        'Cannot access Store.state before Store.init() has been called.'
      );
    }
    return this.storeContext.store.getState() as StoreBoundState<TStateMap>;
  }

  get dispatch(): ReduxStoreContext['store']['dispatch'] {
    if (!this.storeContext) {
      throw new Error(
        'Cannot access Store.dispatch before Store.init() has been called.'
      );
    }
    return this.storeContext.store.dispatch;
  }

  /**
   * Return an existing store context that should prevent re-initialization.
   * Defaults to the instance-level context (guards against double init());
   * framework-specific subclasses can override to consult external contexts.
   */
  protected getExistingStoreContext(): ReduxStoreContext | undefined {
    return this.storeContext;
  }

  protected initStoreContext(initialState?: PreloadedStoreState): ReduxStoreContext | undefined {
    const existingStoreContext = this.getExistingStoreContext();
    if (existingStoreContext) {
      return undefined;
    }

    const middlewares = this.getMiddlewarePipeline();

    const rootReducer = combineReducers(this.reducers as ReducersMap);
    const store = createStore(
      rootReducer,
      this.createExtendedDefaultState(initialState),
      applyMiddleware(...middlewares)
    );
    const storeContext: ReduxStoreContext = {
      store,
    };
    this.storeContext = storeContext;
    this.cadencedStoreStateStream = createCadencedStoreStateStream<StoreBoundState<TStateMap>>(
      store,
      this.getOrCreateSelectorCadenceSource()
    );

    return storeContext;
  }

  init(initialState?: PreloadedStoreState): () => void {
    const storeContext = this.initStoreContext(initialState);
    if (!storeContext) {
      return () => {};
    }

    this.attachLogger();
    this.startSagaManager(storeContext);
    this.startSelectorTraceSummaryInterval();

    return () => {
      this.dispose();
    };
  }

  getSelectorTraceReporter<
    STATE,
    R,
    ARGS extends unknown[] = [],
  >(): SelectorTraceReporter<STATE, R, ARGS> | undefined {
    return this.selectorTraceReporter as SelectorTraceReporter<STATE, R, ARGS> | undefined;
  }

  shouldTraceSelectorCache(): boolean {
    return (
      this.selectorTracingEnabled &&
      (this.selectorTracingOptions.traceCache || this.selectorTraceSummaryCollector !== undefined)
    );
  }

  getSelectorTraceSummary(): SelectorTraceSummary {
    return this.selectorTracingOptions.summaryEnabled
      ? this.selectorTraceSummaryCollector?.snapshot() ?? getEmptySelectorTraceSummary()
      : getEmptySelectorTraceSummary();
  }

  private registerSelectorTracingBridge(): void {
    registerSelectorTracingBridge(this, {
      getComputationTraceOptions: <STATE, R, ARGS extends unknown[]>() => {
        const traceReporter = this.selectorTraceReporter as
          | SelectorTraceReporter<STATE, R, ARGS>
          | undefined;
        if (
          !this.selectorTracingEnabled ||
          !traceReporter ||
          !(
            this.selectorTracingOptions.traceExecution ||
            this.selectorTracingOptions.traceInvalidation ||
            this.selectorTracingOptions.traceArguments ||
            this.selectorTracingOptions.traceResults ||
            this.selectorTraceSummaryCollector !== undefined
          )
        ) {
          return undefined;
        }
        return {
          traceReporter,
          traceExecution:
            this.selectorTracingOptions.traceExecution ||
            this.selectorTracingOptions.summaryEnabled,
          traceInvalidation:
            this.selectorTracingOptions.traceInvalidation ||
            this.selectorTracingOptions.summaryEnabled,
          traceArguments: this.selectorTracingOptions.traceArguments,
          traceResults:
            this.selectorTracingOptions.traceResults ||
            this.selectorTracingOptions.summaryEnabled,
        } satisfies SelectorComputationTraceOptions<STATE, R, ARGS>;
      },
      getCacheTraceReporter: <STATE, R, ARGS extends unknown[]>() =>
        this.selectorTracingEnabled &&
        (this.selectorTracingOptions.traceCache || this.selectorTraceSummaryCollector !== undefined)
          ? (this.selectorTraceReporter as SelectorTraceReporter<STATE, R, ARGS> | undefined)
          : undefined,
    });
  }

  private reportSelectorTrace<STATE, R, ARGS extends unknown[] = []>(
    trace: SelectorTrace<STATE, R, ARGS>
  ): void {
    if (!this.selectorTracingEnabled) {
      return;
    }

    this.selectorTraceSummaryCollector?.record(trace);

    if ('observableCacheRequestCount' in trace) {
      if (!this.selectorTracingOptions.traceCache) {
        return;
      }
      this.publishTrace('selectorDetail', {
        kind: 'cache',
        observableCacheRequestCount: trace.observableCacheRequestCount,
        observableCacheCachedCount: trace.observableCacheCachedCount,
        outputCacheStatus: trace.outputCacheStatus,
        outputCacheRequestCount: trace.outputCacheRequestCount,
        outputCacheHitCount: trace.outputCacheHitCount,
        outputCacheMissCount: trace.outputCacheMissCount,
        selectorSource: getSelectorSourceSnippet(trace.selectorFunc),
      });
      return;
    }

    const reportExecution =
      this.selectorTracingOptions.traceExecution &&
      trace.executionDurationMs !== undefined &&
      trace.executionDurationMs >= this.selectorTracingOptions.minDurationMs &&
      trace.accessedPaths !== undefined &&
      trace.parsedPaths !== undefined;
    const reportInvalidation =
      this.selectorTracingOptions.traceInvalidation && trace.invalidationReason !== undefined;
    const reportArguments =
      this.selectorTracingOptions.traceArguments && trace.argumentsChanged !== undefined;
    const reportResults =
      this.selectorTracingOptions.traceResults && trace.resultOutcome !== undefined;

    if (!reportExecution && !reportInvalidation && !reportArguments && !reportResults) {
      return;
    }

    const payload: Record<string, unknown> = {
      recomputationCount: trace.recomputationCount,
      selectorSource: getSelectorSourceSnippet(trace.selectorFunc),
    };
    if (reportExecution && trace.accessedPaths && trace.parsedPaths) {
      payload.accessedPathCount = trace.accessedPathCount;
      payload.accessedPaths = renderAccessedPaths(trace.accessedPaths, trace.parsedPaths);
      payload.executionDurationMs = trace.executionDurationMs;
    }
    if (reportInvalidation) {
      payload.invalidationReason = trace.invalidationReason;
      payload.changedAccessedPaths =
        trace.changedAccessedPaths && trace.parsedPaths
          ? renderAccessedPaths(trace.changedAccessedPaths, trace.parsedPaths)
          : [];
    }
    if (reportArguments) {
      payload.argumentsChanged = trace.argumentsChanged;
      payload.changedArguments = trace.changedArguments ?? [];
    }
    if (reportResults) {
      payload.resultOutcome = trace.resultOutcome;
    }
    this.publishTrace('selectorDetail', {
      kind: 'selector',
      selectorSource: payload.selectorSource as string,
      ...payload,
    });
  }

  traceSelectors(): void {
    if (
      !this.legacySelectorTracingActivationAllowed ||
      !this.selectorTraceReporter
    ) {
      return;
    }
    this.selectorTracingOptions = normalizeSelectorTracingOptions(true);
    this.selectorTracingEnabled = true;
    this.registerSelectorTracingBridge();
  }

  private startSelectorTraceSummaryInterval(): void {
    if (
      !this.selectorTracingOptions.summaryEnabled ||
      !this.selectorTraceSummaryCollector ||
      this.selectorTraceSummaryInterval !== undefined
    ) {
      return;
    }
    const collector = this.selectorTraceSummaryCollector;
    this.selectorTraceSummaryInterval = setInterval(() => {
      this.publishTrace('selectorSummary', this.getSelectorTraceSummary());
      const period = collector.consumePeriod();
      const selectors = period.filter((summary) => this.isPeriodSummaryEligible(summary));
      if (selectors.length === 0) return;
      const totalExecutionCount = selectors.reduce(
        (total, summary) => total + summary.executionCount,
        0
      );
      const totalRecomputationCount = selectors.reduce(
        (total, summary) => total + summary.recomputationCount,
        0
      );
      const aggregate: SelectorTraceAggregate = Object.freeze({
        intervalMs: this.selectorTracingOptions.summaryIntervalMs,
        selectors: Object.freeze(selectors),
      });
      console.info(
        `[themis] selectors fired: ${totalExecutionCount}, recalculated: ${totalRecomputationCount}`,
        aggregate
      );
    }, this.selectorTracingOptions.summaryIntervalMs);
  }

  private isPeriodSummaryEligible(summary: SelectorTracePeriodSummary): boolean {
    const options = this.selectorTracingOptions;
    const traceExecution = options.traceExecution || options.summaryEnabled;
    const traceCache = options.traceCache || options.summaryEnabled;
    const traceInvalidation = options.traceInvalidation || options.summaryEnabled;
    const traceResults = options.traceResults || options.summaryEnabled;
    const hasInvalidation = Object.values(summary.invalidationReasons).some((count) => count > 0);
    const hasResult = Object.values(summary.resultOutcomes).some((count) => count > 0);
    const executionEligible =
      traceExecution &&
      summary.duration.count > 0 &&
      summary.duration.maximumMs >= options.minDurationMs &&
      summary.recomputationCount >= options.minRecomputationCount;
    const cacheEligible =
      traceCache &&
      summary.cache.requestCount > 0 &&
      summary.cache.missCount >= options.minCacheMissCount;
    return (
      executionEligible ||
      (traceInvalidation && hasInvalidation) ||
      (options.traceArguments && summary.arguments.count > 0) ||
      (traceResults && hasResult) ||
      cacheEligible
    );
  }

  private disposeSelectorTraceSummaryInterval(): void {
    if (this.selectorTraceSummaryInterval !== undefined) {
      clearInterval(this.selectorTraceSummaryInterval);
      this.selectorTraceSummaryInterval = undefined;
    }
    this.selectorTraceSummaryCollector?.resetPeriod();
  }

  protected startSagaManager(storeContext: ReduxStoreContext): void {
    this.runSagaSafely(
      sagaManager,
      storeContext.store,
      (runningTasksContext: ReduxStoreContext['tasks']) => {
        storeContext.tasks = runningTasksContext;
      },
      (event) => {
        this.reportRuntimeError(event.error, event.source, event.message, event.payload);
      }
    );
  }

  initDevTool(): () => void {
    if (!this.storeContext) {
      throw new Error(
        'Cannot initialize Store.initDevTool before Store.init() has been called.'
      );
    }

    this.disposeDevTools = registerGlobalDevTools(this, (event) => {
      this.reportRuntimeError(event.error, event.source, event.message, event.payload);
    });
    return this.disposeDevTools;
  }

  dispose(): void {
    this.disposeLogger();
    evictSelectorOutputsForStateSource(this);
    this.disposeSelectorTraceSummaryInterval();

    if (!this.storeContext) {
      this.disposeCadencedStoreStateStream();
      this.disposeSelectorCadenceSource();
      return;
    }

    this.disposeDevTools?.();
    this.disposeDevTools = undefined;
    this.disposeCadencedStoreStateStream();
    this.stopSagas();
    this.storeContext = undefined;
    this.disposeSelectorCadenceSource();
  }

  runSaga<TSaga extends Saga>(saga: TSaga): () => void {
    const sagaName = deriveSagaName(saga);

    if (!this.storeContext) {
      throw new Error(
        `Cannot run saga "${sagaName}" before Store.init() has been called.`
      );
    }
    if (sagaName === INTERNAL_SAGA_MANAGER_NAME) {
      throw new Error(
        `Saga "${INTERNAL_SAGA_MANAGER_NAME}" is reserved for internal saga management and cannot be run directly.`
      );
    }
    const { store } = this.storeContext;
    store.dispatch(startSaga(sagaName, saga));
    return () => {
      store.dispatch(stopSaga(sagaName));
    };
  }
}
